// src/screens/LandingPage.js
import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * LandingPage - Public homepage for SelfDrive
 *
 * Features:
 * - Hero section with value proposition
 * - CTAs for booking and dealer login
 * - Key features section
 * - How it works section
 * - Fully responsive design
 */
const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Redirect authenticated users to appropriate dashboard
  React.useEffect(() => {
    if (user) {
      if (user.role === 'dealer_admin' || user.role === 'dealer_staff') {
        navigate('/dealer');
      } else if (user.role === 'admin') {
        navigate('/admin/dealers');
      } else if (user.role === 'kiosk') {
        navigate('/kiosk');
      } else {
        navigate('/vehicles');
      }
    }
  }, [user, navigate]);

  // If user is authenticated, don't render landing page (will redirect)
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-app-bg text-app-text">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-app-accent/20 via-transparent to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left column - Text content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Experience Your
                <span className="block text-app-accent">Dream Car Today</span>
              </h1>
              <p className="text-lg sm:text-xl text-app-text-secondary-light dark:text-app-text-secondary mb-8 max-w-2xl mx-auto lg:mx-0">
                Book test drives instantly from top dealerships. No waiting, no hassle.
                Your perfect vehicle is just a click away.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/vehicles"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl bg-app-accent text-white hover:bg-blue-600 transition-colors shadow-lg shadow-app-accent/30"
                >
                  <span className="mr-2">🚗</span>
                  Browse Vehicles
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text hover:opacity-80 transition-colors border border-app-border-light dark:border-app-border"
                >
                  Dealer Login
                </Link>
              </div>

              {/* Quick stats */}
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto lg:mx-0">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-app-accent">500+</div>
                  <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mt-1">Vehicles</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-app-accent">100+</div>
                  <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mt-1">Dealerships</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-app-accent">10K+</div>
                  <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mt-1">Test Drives</div>
                </div>
              </div>
            </div>

            {/* Right column - Hero image placeholder */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Decorative background */}
                <div className="absolute inset-0 bg-gradient-to-r from-app-accent/30 to-purple-500/30 rounded-3xl blur-3xl"></div>

                {/* Image placeholder - using gradient as placeholder */}
                <div className="relative bg-gradient-to-br from-app-bg-secondary-light dark:from-app-bg-secondary to-app-bg-light dark:to-app-bg rounded-3xl overflow-hidden aspect-[4/3] border border-app-border-light dark:border-app-border flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🚗</div>
                    <p className="text-app-text-secondary-light dark:text-app-text-secondary">Your Dream Car Awaits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-black/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Choose SelfDrive?</h2>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary text-lg max-w-2xl mx-auto">
              We make test drive booking simple, fast, and convenient
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Instant Booking</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Book your test drive in seconds. Real-time availability from multiple dealerships.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Search</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Filter by make, model, year, price, and type to find your perfect match.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Digital Experience</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Complete digital waiver, QR code check-in, and booking management on any device.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">⭐</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Save Favorites</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Create a wishlist of vehicles you love and book them when you're ready.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">📅</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Flexible Scheduling</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Choose from available time slots that fit your schedule. Reschedule anytime.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border hover:border-app-accent/50 transition-colors">
              <div className="w-12 h-12 bg-app-accent/20 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">🏢</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Multiple Dealers</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary">
                Access inventory from multiple dealerships in one convenient platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary text-lg max-w-2xl mx-auto">
              Get behind the wheel in just a few simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-app-accent/0 via-app-accent/50 to-app-accent/0"></div>

            {/* Step 1 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-app-accent rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10">
                1
              </div>
              <h3 className="text-lg font-semibold mb-2">Browse Vehicles</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm">
                Search our extensive catalog and find vehicles that match your preferences
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-app-accent rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10">
                2
              </div>
              <h3 className="text-lg font-semibold mb-2">Select Time Slot</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm">
                Choose an available time that works for your schedule
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-app-accent rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10">
                3
              </div>
              <h3 className="text-lg font-semibold mb-2">Complete Waiver</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm">
                Fill out the digital waiver quickly and securely
              </p>
            </div>

            {/* Step 4 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-app-accent rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10">
                4
              </div>
              <h3 className="text-lg font-semibold mb-2">Test Drive</h3>
              <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm">
                Check in at the dealership and enjoy your test drive
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-app-accent/20 to-purple-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Find Your Perfect Vehicle?
          </h2>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary text-lg mb-8">
            Join thousands of satisfied customers who found their dream car with SelfDrive
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vehicles"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl bg-app-accent text-white hover:bg-blue-600 transition-colors shadow-lg shadow-app-accent/30"
            >
              Start Browsing
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text hover:opacity-80 transition-colors border border-app-border-light dark:border-app-border"
            >
              Create Account
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-app-bg-light/40 dark:bg-black/40 border-t border-app-border-light dark:border-app-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-app-text-secondary-light dark:text-app-text-secondary text-sm">
            <p>&copy; 2024 SelfDrive. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
